import type { ParsedLink } from "../types";

export type GeniusDeeplinkParams = {
  signedToken: string;
  scheduleId?: string;
  cohortAsmId?: string;
  examId?: string;
  examType?: string;
  grade?: string;
  eventName?: string;
  utmSource?: string;
  utmCampaign?: string;
};

const SIGNED_TOKEN_KEYS = [
  "token",
  "signedtokens",
  "signedTokens",
  "signed-tokens",
  "signed_tokens",
  "signedtoken",
];

export const GENIUS_OLYMPIAD_ASSESSMENT_LABEL = "Genius Olympiad Assessment";
export const GENIUS_DEEPLINK_PATH = "genius-deeplink";

export function isGeniusDeeplinkEntryPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  return (
    normalized === `/${GENIUS_DEEPLINK_PATH}` ||
    normalized.startsWith(`/${GENIUS_DEEPLINK_PATH}/`)
  );
}

const GENIUS_ASSESSMENT_EXAM_TYPES = new Set([
  "genius assessment",
  "genius assesment",
  "genius olympiad assessment",
]);

/**
 * Genius links sometimes arrive as path-style query strings:
 *   /genius-deeplink/module=tests&examtype=Genius+Olympiad+Assessment&token=...
 * instead of:
 *   /genius-deeplink?module=tests&examtype=Genius+Olympiad+Assessment&token=...
 * React Router then misses the route and the page stays blank.
 */
function extractEmbeddedQuery(pathname: string): string | null {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const marker = `/${GENIUS_DEEPLINK_PATH}/`;
  const markerIdx = normalized.indexOf(marker);
  if (markerIdx >= 0) {
    const embedded = normalized.slice(markerIdx + marker.length);
    if (embedded.includes("=")) return embedded;
  }

  // Root path-style: /module=tests&examtype=...&token=...
  if (normalized.startsWith("/") && normalized.includes("=")) {
    const withoutSlash = normalized.slice(1);
    if (
      withoutSlash.includes("examtype=") ||
      withoutSlash.includes("examType=") ||
      SIGNED_TOKEN_KEYS.some((key) => withoutSlash.includes(`${key}=`))
    ) {
      return withoutSlash;
    }
  }

  return null;
}

function readSearchParams(
  input: string | URLSearchParams | ParsedLink,
): URLSearchParams {
  if (input instanceof URLSearchParams) return input;
  if (typeof input === "string") {
    try {
      const url = new URL(
        input,
        typeof window !== "undefined"
          ? window.location.origin
          : "http://localhost",
      );
      if ([...url.searchParams.keys()].length > 0) {
        return url.searchParams;
      }

      const embedded = extractEmbeddedQuery(url.pathname);
      if (embedded) {
        return new URLSearchParams(embedded);
      }

      return url.searchParams;
    } catch {
      // Bare query / path-style fragment without a host.
      if (input.includes("=")) {
        const normalized = input.startsWith("?") ? input.slice(1) : input;
        const pathEmbedded = extractEmbeddedQuery(
          normalized.startsWith("/") ? normalized : `/${normalized}`,
        );
        return new URLSearchParams(pathEmbedded ?? normalized);
      }
      return new URLSearchParams();
    }
  }
  return new URLSearchParams(input.allParams ?? {});
}

function readExamType(params: URLSearchParams): string | undefined {
  const raw =
    params.get("examtype")?.trim() ||
    params.get("examType")?.trim() ||
    undefined;
  if (!raw) return undefined;
  if (isGeniusAssessmentExamType(raw)) {
    return GENIUS_OLYMPIAD_ASSESSMENT_LABEL;
  }
  return raw;
}

function normalizeGeniusExamTypeKey(examType: string): string {
  // Web URLSearchParams turns + into space; some Android/path parsers keep +.
  return examType.trim().replace(/\+/g, " ").replace(/\s+/g, " ").toLowerCase();
}

export function isGeniusAssessmentExamType(examType?: string | null): boolean {
  if (!examType?.trim()) return false;
  return GENIUS_ASSESSMENT_EXAM_TYPES.has(normalizeGeniusExamTypeKey(examType));
}

function readSignedToken(params: URLSearchParams): string | undefined {
  for (const key of SIGNED_TOKEN_KEYS) {
    const value = params.get(key)?.trim();
    if (value) return value;
  }
  return undefined;
}

export function isGeniusDeeplink(
  input: string | URLSearchParams | ParsedLink | null | undefined,
): boolean {
  if (!input) return false;
  const params = readSearchParams(input);
  return (
    isGeniusAssessmentExamType(readExamType(params)) &&
    Boolean(readSignedToken(params))
  );
}

export function parseGeniusDeeplink(
  input: string | URLSearchParams | ParsedLink | null | undefined,
): GeniusDeeplinkParams | null {
  if (!isGeniusDeeplink(input)) return null;

  const params = readSearchParams(input!);
  const signedToken = readSignedToken(params);
  if (!signedToken) return null;

  return {
    signedToken,
    scheduleId:
      params.get("scheduleId")?.trim() ||
      params.get("scheduleid")?.trim() ||
      undefined,
    cohortAsmId:
      params.get("cohortAsmId")?.trim() ||
      params.get("cohortasmid")?.trim() ||
      params.get("cohortId")?.trim() ||
      params.get("cohortid")?.trim() ||
      params.get("testseriesAsmId")?.trim() ||
      params.get("testSeriesAsmId")?.trim() ||
      params.get("series_id")?.trim() ||
      undefined,
    examId:
      params.get("examid")?.trim() ||
      params.get("examId")?.trim() ||
      params.get("testId")?.trim() ||
      params.get("ruleId")?.trim() ||
      params.get("ruleid")?.trim() ||
      undefined,
    examType: readExamType(params),
    grade: params.get("grade")?.trim() || undefined,
    eventName:
      params.get("eventName")?.trim() ||
      params.get("eventname")?.trim() ||
      "Event 2026",
    utmSource:
      params.get("utm_source")?.trim() ||
      params.get("utmSource")?.trim() ||
      undefined,
    utmCampaign:
      params.get("utm_campaign")?.trim() ||
      params.get("utmCampaign")?.trim() ||
      undefined,
  };
}

export function buildGeniusDeeplinkPath(search: string): string {
  const normalized = search.startsWith("?")
    ? search
    : search
      ? `?${search}`
      : "";
  return `/${GENIUS_DEEPLINK_PATH}${normalized}`;
}

export function buildGeniusDeeplinkPathFromParams(
  params: GeniusDeeplinkParams,
): string {
  const query = new URLSearchParams();
  query.set("token", params.signedToken);
  if (params.scheduleId) query.set("scheduleId", params.scheduleId);
  if (params.cohortAsmId) query.set("cohortAsmId", params.cohortAsmId);
  if (params.examId) query.set("examId", params.examId);
  query.set(
    "examtype",
    params.examType?.trim() || GENIUS_OLYMPIAD_ASSESSMENT_LABEL,
  );
  if (params.grade) query.set("grade", params.grade);
  if (params.eventName) query.set("eventName", params.eventName);
  if (params.utmSource) query.set("utm_source", params.utmSource);
  if (params.utmCampaign) query.set("utm_campaign", params.utmCampaign);
  return buildGeniusDeeplinkPath(`?${query.toString()}`);
}

/**
 * Force `/genius-deeplink?<params>` shape. Fixes path-style deeplinks that
 * leave React on an unmatched route (blank page).
 */
export function normalizeGeniusDeeplinkLocation(): boolean {
  if (typeof window === "undefined") return false;

  const href = window.location.href;
  const params = parseGeniusDeeplink(href);
  if (!params) return false;

  const target = buildGeniusDeeplinkPathFromParams(params);
  const currentPath = window.location.pathname.replace(/\/+$/, "") || "/";
  const currentSearch = window.location.search;
  const desired = new URL(target, window.location.origin);

  if (
    currentPath === `/${GENIUS_DEEPLINK_PATH}` &&
    currentSearch === desired.search
  ) {
    return false;
  }

  window.history.replaceState(null, "", target);
  return true;
}

export function redirectToGeniusDeeplinkIfNeeded(): boolean {
  if (typeof window === "undefined") return false;
  if (!isGeniusDeeplink(window.location.href)) return false;
  return normalizeGeniusDeeplinkLocation();
}

/**
 * Base URL / non-Genius entry while OTP is still pending → open Genius deeplink route.
 * Always rebuilds from persisted params so path-style original URLs cannot break routing.
 */
export function redirectToPersistedGeniusDeeplinkIfNeeded(
  originalUrl: string | null,
  params: {
    signedToken: string;
    scheduleId?: string;
    cohortAsmId?: string;
    examId?: string;
    examType?: string;
    grade?: string;
    eventName?: string;
  } | null,
): boolean {
  if (typeof window === "undefined") return false;

  const currentPath = window.location.pathname.replace(/\/+$/, "") || "/";
  if (
    currentPath === `/${GENIUS_DEEPLINK_PATH}` &&
    isGeniusDeeplink(window.location.href)
  ) {
    normalizeGeniusDeeplinkLocation();
    return false;
  }

  // Already a Genius URL — soft-normalize only; caller must still mount React.
  if (isGeniusDeeplink(window.location.href)) {
    redirectToGeniusDeeplinkIfNeeded();
    return false;
  }

  const fromOriginal = originalUrl ? parseGeniusDeeplink(originalUrl) : null;
  const resolved = params?.signedToken
    ? (params as GeniusDeeplinkParams)
    : fromOriginal;

  if (!resolved?.signedToken) return false;

  window.location.replace(buildGeniusDeeplinkPathFromParams(resolved));
  return true;
}

/** Safe resume path for SPA Navigate — always uses `?`, never path-style. */
export function resolveGeniusResumePath(
  originalUrl: string | null,
  params: GeniusDeeplinkParams | null,
): string | null {
  const fromOriginal = originalUrl ? parseGeniusDeeplink(originalUrl) : null;
  const resolved = params?.signedToken ? params : fromOriginal;
  if (!resolved?.signedToken) return null;
  return buildGeniusDeeplinkPathFromParams(resolved);
}
